"use client";

import { useState } from "react";
import { LuSlidersHorizontal } from "react-icons/lu";
import {
  TEAM_SIZE_BUCKETS,
  type AgencyFilters,
  type TeamSizeBucket,
} from "@/hooks/useAgencyFilters";

const COUNTRY_LABEL: Record<string, string> = { "United States": "USA" };
const countryLabel = (c: string) => COUNTRY_LABEL[c] ?? c;

const LIMIT = 5;

function ShowMoreList({
  items,
  selected,
  onToggle,
  emptyText,
  display,
}: {
  items: string[];
  selected: string[];
  onToggle: (v: string) => void;
  emptyText: string;
  display?: (v: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, LIMIT);
  const overflow = items.length - LIMIT;

  if (items.length === 0) {
    return <p className="text-xs text-gray-400">{emptyText}</p>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {visible.map((v) => (
        <label
          key={v}
          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none"
        >
          <input
            type="checkbox"
            checked={selected.includes(v)}
            onChange={() => onToggle(v)}
            className="rounded border-gray-300 dark:border-white/20 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="truncate">{display ? display(v) : v}</span>
        </label>
      ))}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-0.5 text-left text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          {expanded ? "Show less" : `+${overflow} more`}
        </button>
      )}
    </div>
  );
}

export type AgencyFilterPanelProps = {
  countries: string[];
  fields: string[];
  services: string[];
  filters: AgencyFilters;
  setFilters: (
    update:
      | Partial<AgencyFilters>
      | ((prev: AgencyFilters) => AgencyFilters)
  ) => void;
  clear: () => void;
  activeCount: number;
};

export function AgencyFilterPanel({
  countries,
  fields,
  services,
  filters,
  setFilters,
  clear,
  activeCount,
}: AgencyFilterPanelProps) {
  const toggleArray = (key: "countries" | "fields" | "services", v: string) =>
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key].includes(v)
        ? prev[key].filter((x) => x !== v)
        : [...prev[key], v],
    }));

  const toggleTeamSize = (b: TeamSizeBucket) =>
    setFilters((prev) => ({
      ...prev,
      teamSizes: prev.teamSizes.includes(b)
        ? prev.teamSizes.filter((x) => x !== b)
        : [...prev.teamSizes, b],
    }));

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LuSlidersHorizontal className="w-4 h-4 text-gray-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm uppercase tracking-wide">
            Filters
          </h3>
        </div>
        {activeCount > 0 && (
          <button
            onClick={clear}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Countries */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Countries covered
        </h4>
        <ShowMoreList
          items={countries}
          selected={filters.countries}
          onToggle={(v) => toggleArray("countries", v)}
          emptyText="No countries in dataset"
          display={countryLabel}
        />
      </div>

      {/* Fields */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Fields covered
        </h4>
        <ShowMoreList
          items={fields}
          selected={filters.fields}
          onToggle={(v) => toggleArray("fields", v)}
          emptyText="No fields in dataset"
        />
      </div>

      {/* Services */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Services offered
        </h4>
        <ShowMoreList
          items={services}
          selected={filters.services}
          onToggle={(v) => toggleArray("services", v)}
          emptyText="No services in dataset"
        />
      </div>

      {/* Rating */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            Minimum rating
          </h4>
          <span className="text-xs text-gray-500">{filters.minRating.toFixed(1)}+</span>
        </div>
        <input
          type="range"
          min={0}
          max={5}
          step={0.1}
          value={filters.minRating}
          onChange={(e) => setFilters({ minRating: parseFloat(e.target.value) })}
          className="w-full accent-indigo-600"
          aria-label="Minimum rating"
        />
      </div>

      {/* Team size */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Team size
        </h4>
        <div className="flex flex-col gap-1.5">
          {TEAM_SIZE_BUCKETS.map((b) => (
            <label
              key={b}
              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none"
            >
              <input
                type="checkbox"
                checked={filters.teamSizes.includes(b)}
                onChange={() => toggleTeamSize(b)}
                className="rounded border-gray-300 dark:border-white/20 text-indigo-600 focus:ring-indigo-500"
              />
              <span>{b}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
